import { useQuery } from "@apollo/client";
import moment from "moment";
import { useMemo } from "react";
import {
  GetDeliveredEngagementSurveysQuery,
  GetDeliveredEngagementSurveysQueryVariables,
} from "types/graphql-schema";

import { getProgramUrl } from "@apps/programs/helpers";
import { currentOrganizationVar } from "@cache/cache";
import Button from "@components/button/button";
import Layout from "@components/layout/layout";
import { TopicflowIcon } from "@components/topicflow-icon/topicflow-icon";
import { assertEdgesNonNull } from "@helpers/helpers";

import getDeliveredEngagementSurveysQuery from "../graphql/get-delivered-engagement-surveys-query";

const EngagementSurveyHomepageDeliveries = () => {
  const currentOrganization = currentOrganizationVar();
  const { data, loading, fetchMore } = useQuery<
    GetDeliveredEngagementSurveysQuery,
    GetDeliveredEngagementSurveysQueryVariables
  >(getDeliveredEngagementSurveysQuery, {
    notifyOnNetworkStatusChange: true,
    variables: {
      organizationId: currentOrganization.id,
      after: null,
      orderBy: "-submitted_datetime",
    },
  });
  const deliveries = useMemo(() => {
    return data?.engagementSurveyDeliveries
      ? assertEdgesNonNull(data?.engagementSurveyDeliveries)
      : [];
  }, [data]);

  const handleClickLoadMore = () => {
    if (data?.engagementSurveyDeliveries?.pageInfo?.hasNextPage) {
      fetchMore({
        variables: {
          after: data?.engagementSurveyDeliveries?.pageInfo?.endCursor,
          merge: true,
        },
      });
    }
  };

  return (
    <Layout.MainSubSection
      className="mt-8"
      title=""
      loading={loading}
      emptyPlaceholder={
        deliveries.length === 0
          ? `There are no delivered Surveys for you`
          : null
      }
    >
      <Layout.MainSubSectionList>
        {deliveries.map((delivery) => {
          const reportingUrl = delivery.complianceProgram
            ? getProgramUrl(delivery.complianceProgram.id, {
                isSurvey: true,
                subPath: "/reporting/engagement?tab=raw",
              })
            : undefined;
          return (
            <Layout.MainSubSectionListItem key={delivery.id}>
              <div className="flex items-center justify-between text-sm gap-4">
                <div className="flex items-center gap-3">
                  <TopicflowIcon type="survey" className="shrink-0 w-5 h-5" />
                  <div className="font-medium">
                    {delivery.complianceProgram?.title}
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="text-gray-400">
                    {delivery.lastSubmissionDatetime
                      ? `Last submission on ${moment(
                          delivery.lastSubmissionDatetime,
                        ).format("MMM Do, YYYY")}`
                      : ""}
                  </div>
                  <Button text="View" to={reportingUrl} />
                </div>
              </div>
            </Layout.MainSubSectionListItem>
          );
        })}
        {data?.engagementSurveyDeliveries?.pageInfo.hasNextPage && (
          <Layout.MainSubSectionListItem className="flex justify-center">
            <Button text="Load more" onClick={handleClickLoadMore} />
          </Layout.MainSubSectionListItem>
        )}
      </Layout.MainSubSectionList>
    </Layout.MainSubSection>
  );
};

export default EngagementSurveyHomepageDeliveries;
